import { Trans, useLingui } from '@lingui/react/macro';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect, useRouter } from '@tanstack/react-router';
import { type ChangeEvent, type FormEvent, useEffect, useRef, useState } from 'react';
import { magToast } from '../components/toaster.js';
import { WorkspaceNav } from '../components/workspace-nav.js';
import {
  type MeResponse,
  updateMyLocaleApi,
  updateProfileApi,
  uploadAvatarApi,
} from '../lib/api.js';
import { founderErrorCopy } from '../lib/error-copy.js';
import { DASHBOARD_LOCALES } from '../lib/i18n.js';
import { meQuery } from '../lib/queries.js';
import { refreshQueries } from '../lib/refresh.js';

// Per-user profile page. Available to every signed-in user regardless
// of workspace role — your own profile is yours.

const AVATAR_MAX_BYTES = 2 * 1024 * 1024;
const AVATAR_MIME_ALLOWLIST = ['image/png', 'image/jpeg', 'image/webp'];

export const Route = createFileRoute('/profile')({
  beforeLoad: async ({ context: { queryClient } }) => {
    const me = await queryClient.ensureQueryData(meQuery());
    if (!me) throw redirect({ to: '/login' });
    return { me };
  },
  loader: ({ context }) => context as { me: MeResponse },
  component: ProfilePage,
});

function ProfilePage() {
  const { t } = useLingui();

  const { me } = Route.useLoaderData();
  const queryClient = useQueryClient();
  const router = useRouter();

  const [name, setName] = useState(me.name);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  useEffect(() => {
    setName(me.name);
  }, [me.name]);

  const dirty = name.trim() !== me.name.trim() && name.trim().length > 0;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!dirty) return;
    setSaving(true);
    try {
      await updateProfileApi({ name: name.trim() });
      await refreshQueries(queryClient, ['me']);
      await router.invalidate();
      magToast.success('saved.', "i'll use that name from now on.");
    } catch (err) {
      magToast.error("couldn't save.", founderErrorCopy(err, 'try again.'));
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarFile = async (file: File) => {
    if (file.size === 0) {
      magToast.error("that file's empty.", 'pick another.');
      return;
    }
    if (file.size > AVATAR_MAX_BYTES) {
      magToast.error('too big.', `keep avatars under ${AVATAR_MAX_BYTES / 1024 / 1024} MB.`);
      return;
    }
    if (!AVATAR_MIME_ALLOWLIST.includes(file.type)) {
      magToast.error("i don't speak that format.", "png, jpeg, or webp — that's the list.");
      return;
    }
    setUploadingAvatar(true);
    try {
      await uploadAvatarApi(file);
      await refreshQueries(queryClient, ['me']);
      await router.invalidate();
      magToast.success('new look.', 'avatar updated.');
    } catch (err) {
      magToast.error("couldn't upload.", founderErrorCopy(err, 'try again.'));
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleRemoveAvatar = async () => {
    setUploadingAvatar(true);
    try {
      await updateProfileApi({ image: null });
      await refreshQueries(queryClient, ['me']);
      await router.invalidate();
      magToast.success('cleared.', 'falling back to initials.');
    } catch (err) {
      magToast.error("couldn't remove.", founderErrorCopy(err, 'try again.'));
    } finally {
      setUploadingAvatar(false);
    }
  };

  return (
    <main className="min-h-screen bg-[var(--cream)]">
      <WorkspaceNav />
      <section className="mx-auto max-w-[720px] px-7 pb-16 pt-8">
        <header>
          <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
            {'↘'} <Trans>profile</Trans>
          </p>
          <h1 className="mt-2 font-display text-[34px] font-black leading-[1.05] tracking-[-0.04em]">
            <Trans>your profile.</Trans>
          </h1>
          <p className="mt-3 max-w-[600px] text-[14.5px] leading-[1.5] text-[var(--ink)]/70">
            <Trans>mag uses this name when she addresses you and on the team page.</Trans>
          </p>
        </header>

        <article className="mt-7 rounded-2xl border border-[var(--line)] bg-white p-5">
          <h2 className="font-display text-[18px] font-bold tracking-[-0.01em]">
            <Trans>avatar</Trans>
          </h2>
          <p className="mt-1 text-[13px] text-[var(--ink)]/65">
            <Trans>png, jpeg, or webp. 2 MB max.</Trans>
          </p>
          <AvatarRow
            image={me.image}
            name={me.name}
            email={me.email}
            busy={uploadingAvatar}
            onSelectFile={handleAvatarFile}
            onRemove={me.image ? handleRemoveAvatar : undefined}
          />
        </article>

        <article className="mt-6 rounded-2xl border border-[var(--line)] bg-white p-5">
          <h2 className="font-display text-[18px] font-bold tracking-[-0.01em]">
            <Trans>identity</Trans>
          </h2>
          <form onSubmit={handleSubmit} className="mt-4 flex flex-col gap-3">
            <label className="field">
              <span className="field-label">
                <Trans>name</Trans>
              </span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={120}
                autoComplete="name"
                placeholder={t`alex chen`}
              />
            </label>
            <label className="field">
              <span className="field-label">
                <Trans>email</Trans>
              </span>
              <input type="email" value={me.email} disabled aria-readonly className="opacity-70" />
              <span className="field-hint">
                <Trans>
                  email changes aren't supported yet — ping support if you need to swap.
                </Trans>
              </span>
            </label>
            <div className="flex justify-end">
              <button type="submit" disabled={!dirty || saving} className="btn disabled:opacity-50">
                {saving ? <Trans>saving…</Trans> : <Trans>save</Trans>}
                {!saving ? <span className="btn-arrow">→</span> : null}
              </button>
            </div>
          </form>
        </article>

        <LanguageCard locale={me.locale} localeSource={me.localeSource} />
      </section>
    </main>
  );
}

/** [i18n] Interface language. Lives on /profile rather than
 *  /settings/workspace because it is a per-USER setting and that page is
 *  owner-gated — putting it there would hide it from every member and
 *  admin, who read the same interface.
 *
 *  Offers "follow my browser" as a real option, not just en/es. Without it
 *  a founder who once picked Spanish could never go back to following
 *  their browser; they could only pick English, which is a different
 *  statement and produces a different result when they travel or change
 *  device. localeSource is what makes that distinction visible. */
const LOCALE_LABELS: Record<string, string> = {
  en: 'english',
  es: 'español',
};

function LanguageCard({
  locale,
  localeSource,
}: {
  locale: string;
  localeSource: 'user' | 'header' | 'default';
}) {
  const queryClient = useQueryClient();
  const router = useRouter();
  const [saving, setSaving] = useState(false);
  // '' is the "follow my browser" option — an explicit null on the wire.
  const selected = localeSource === 'user' ? locale : '';

  const handleChange = async (e: ChangeEvent<HTMLSelectElement>) => {
    const next = e.target.value === '' ? null : e.target.value;
    setSaving(true);
    try {
      const updated = await updateMyLocaleApi(next);
      await refreshQueries(queryClient, ['me']);
      await router.invalidate();
      magToast.success(
        'language saved.',
        next === null
          ? `following your browser — that's ${LOCALE_LABELS[updated.locale] ?? updated.locale} right now.`
          : `i'll speak ${LOCALE_LABELS[updated.locale] ?? updated.locale} from here.`,
      );
    } catch (err) {
      magToast.error("couldn't save that.", founderErrorCopy(err, 'try again.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <article className="mt-6 rounded-2xl border border-[var(--line)] bg-white p-5">
      <h2 className="font-display text-[18px] font-bold tracking-[-0.01em]">
        <Trans>language</Trans>
      </h2>
      <p className="mt-1 text-[13px] text-[var(--ink)]/65">
        <Trans>
          what i write to you in — the dashboard, your emails, and the memos. it doesn't change the
          language your ads run in; that follows each brand's market.
        </Trans>
      </p>
      <label className="field mt-4">
        <span className="field-label">
          <Trans>interface language</Trans>
        </span>
        <select value={selected} onChange={handleChange} disabled={saving}>
          <option value="">
            <Trans>follow my browser</Trans>
          </option>
          {DASHBOARD_LOCALES.map((code) => (
            <option key={code} value={code}>
              {LOCALE_LABELS[code] ?? code}
            </option>
          ))}
        </select>
        <span className="field-hint">
          {localeSource === 'user' ? (
            <Trans>you picked this one.</Trans>
          ) : (
            <Trans>
              following your browser — that's {LOCALE_LABELS[locale] ?? locale} right now.
            </Trans>
          )}
        </span>
      </label>
    </article>
  );
}

function AvatarRow({
  image,
  name,
  email,
  busy,
  onSelectFile,
  onRemove,
}: {
  image: string | null;
  name: string;
  email: string;
  busy: boolean;
  onSelectFile: (file: File) => Promise<void> | void;
  onRemove?: (() => Promise<void> | void) | undefined;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const fallback = (name?.trim().charAt(0) || email?.trim().charAt(0) || '✦').toUpperCase();

  const handlePick = () => {
    if (busy) return;
    fileRef.current?.click();
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void onSelectFile(file);
    // Reset so picking the same file twice still triggers onChange.
    e.target.value = '';
  };

  return (
    <div className="mt-4 flex items-center gap-4">
      <div className="grid h-20 w-20 shrink-0 place-items-center overflow-hidden rounded-full border border-[var(--line)] bg-[var(--plum)] text-[var(--cream)]">
        {image ? (
          <img
            src={image}
            alt=""
            referrerPolicy="no-referrer"
            className="h-full w-full object-cover"
          />
        ) : (
          <span className="font-display text-[28px] font-bold">{fallback}</span>
        )}
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handlePick}
            disabled={busy}
            className="btn btn-ghost disabled:opacity-60"
          >
            {busy ? (
              <Trans>uploading…</Trans>
            ) : image ? (
              <Trans>replace</Trans>
            ) : (
              <Trans>upload</Trans>
            )}
            {!busy ? <span className="btn-arrow">→</span> : null}
          </button>
          {onRemove ? (
            <button
              type="button"
              onClick={() => void onRemove()}
              disabled={busy}
              className="link-quiet text-[12px] disabled:opacity-60"
            >
              <Trans>remove</Trans>
            </button>
          ) : null}
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          onChange={handleChange}
          className="hidden"
        />
      </div>
    </div>
  );
}
